'use client';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ReactNode } from 'react';

export interface LanguageSwitcherOption {
    code: string;
    label: string;
    nativeName?: string;
    icon?: string;
    href?: string;
    disabled?: boolean;
}

export interface LanguageSwitcherProps {
    languages: LanguageSwitcherOption[];
    currentLanguageCode: string;
    showNativeName?: boolean;
    className?: string;
    buttonClassName?: string;
    menuClassName?: string;
    optionClassName?: string;
    renderCurrentLanguage?: (language: LanguageSwitcherOption) => ReactNode;
    renderLanguageOption?: (language: LanguageSwitcherOption) => ReactNode;
    onSelect?: (languageCode: string) => void;
    id?: string;
}

const ChevronDown = () => (
    <svg
        aria-hidden="true"
        className="size-4 text-gray-400"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M6.25 7.5L10 11.25L13.75 7.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export function LanguageSwitcher({
    languages,
    currentLanguageCode,
    showNativeName = false,
    className,
    buttonClassName,
    menuClassName,
    optionClassName,
    renderCurrentLanguage,
    renderLanguageOption,
    onSelect,
    id,
}: LanguageSwitcherProps) {
    // Используем стабильный ID на основе currentLanguageCode для избежания проблем с гидратацией
    // Если id не передан, создаем стабильный ID на основе currentLanguageCode
    const stableId = id ?? `language-switcher-${currentLanguageCode}`;
    const buttonId = `${stableId}-trigger`;
    const menuId = `${buttonId}-menu`;

    if (!languages || languages.length === 0) {
        return null;
    }

    const fallBackLanguage = languages[0];
    const currentLanguage =
        languages.find((lang) => lang.code === currentLanguageCode) ?? fallBackLanguage;

    const handleSelect = (languageCode: string) => {
        if (onSelect) {
            onSelect(languageCode);
        }
    };

    const defaultRenderOption = (lang: LanguageSwitcherOption) => (
        <>
            {lang.icon && (
                <img
                    src={lang.icon}
                    alt={lang.label}
                    className="size-5 shrink-0 rounded-full object-cover"
                    aria-hidden="true"
                />
            )}
            <span className="flex min-w-0 flex-col">
                <span className="truncate">{lang.label}</span>
                {showNativeName && lang.nativeName && (
                    <span className="truncate text-xs text-gray-500">{lang.nativeName}</span>
                )}
            </span>
        </>
    );

    const renderTriggerContent = (): ReactNode => {
        if (renderCurrentLanguage) {
            return renderCurrentLanguage(currentLanguage);
        }

        return (
            <span className="flex items-center gap-2">
                {currentLanguage.icon && (
                    <img
                        src={currentLanguage.icon}
                        alt={currentLanguage.label}
                        className="size-5 rounded-full object-cover"
                        aria-hidden="true"
                    />
                )}
                <span className="truncate">{currentLanguage.label}</span>
            </span>
        );
    };

    return (
        <Menu as="div" className={`relative inline-block text-left ${className ?? ''}`}>
            <div className="flex items-center">
                <MenuButton
                    className={`inline-flex w-max items-center justify-center gap-x-1.5 rounded-xl bg-white px-3 py-3 text-sm font-medium text-gray-900 shadow-xs hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200 ${buttonClassName ?? ''}`}
                    id={buttonId}
                    aria-controls={menuId}
                >
                    {renderTriggerContent()}
                    <ChevronDown />
                </MenuButton>
            </div>

            <MenuItems
                transition
                className={`absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in ${menuClassName ?? ''}`}
                aria-labelledby={buttonId}
                aria-orientation="vertical"
                id={menuId}
            >
                <div className="py-1">
                    {languages.map((lang) => (
                        <MenuItem key={lang.code} disabled={lang.disabled}>
                            {({ active, disabled }) => {
                                const isCurrent = lang.code === currentLanguage.code;
                                const optionContent = renderLanguageOption
                                    ? renderLanguageOption(lang)
                                    : defaultRenderOption(lang);

                                const commonClasses =
                                    'flex w-full items-center gap-2 px-4 py-2 text-sm transition-colors';
                                const stateClasses = disabled
                                    ? 'cursor-not-allowed text-gray-300'
                                    : active
                                    ? 'bg-gray-100 text-gray-900'
                                    : 'text-gray-700';
                                const selectedClasses = isCurrent ? 'font-semibold' : '';
                                const mergedClasses = `${commonClasses} ${stateClasses} ${selectedClasses} ${
                                    optionClassName ?? ''
                                }`;

                                if (!onSelect && lang.href) {
                                    return (
                                        <a
                                            href={lang.href}
                                            className={mergedClasses}
                                        >
                                            {optionContent}
                                        </a>
                                    );
                                }

                                return (
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(lang.code)}
                                        className={mergedClasses}
                                        disabled={disabled}
                                    >
                                        {optionContent}
                                    </button>
                                );
                            }}
                        </MenuItem>
                    ))}
                </div>
            </MenuItems>
        </Menu>
    );
}
